import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TiposDemissaoPage = () => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: ''
  });

  useEffect(() => {
    loadTipos();
  }, []);

  const loadTipos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tipos-demissao`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTipos(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de demissão:', error);
      toast.error('Erro ao carregar tipos de demissão');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingId) {
        await axios.put(`${API}/api/tipos-demissao/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tipo de demissão atualizado com sucesso!');
      } else {
        await axios.post(`${API}/api/tipos-demissao`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tipo de demissão criado com sucesso!');
      }
      
      closeModal();
      loadTipos();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar tipo de demissão');
    }
  };

  const handleEdit = (tipo) => {
    setEditingId(tipo.id);
    setFormData({
      nome: tipo.nome,
      descricao: tipo.descricao || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente deletar este tipo de demissão?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/tipos-demissao/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tipo de demissão deletado com sucesso!');
      loadTipos();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao deletar tipo de demissão');
    }
  };

  const openNewModal = () => {
    setFormData({ nome: '', descricao: '' });
    setEditingId(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setFormData({ nome: '', descricao: '' });
    setEditingId(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Tipos de Demissão</h1>
          <p className="text-slate-600 mt-1">Gerencie os tipos de demissão da empresa</p>
        </div>
        <Button onClick={openNewModal} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Novo Tipo
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tipos Cadastrados</CardTitle>
          <CardDescription>
            Total: {tipos.length} {tipos.length === 1 ? 'tipo' : 'tipos'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tipos.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>Nenhum tipo de demissão cadastrado.</p>
              <p className="text-sm mt-2">Clique em "Novo Tipo" para começar.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tipos.map((tipo) => (
                <div
                  key={tipo.id}
                  className="flex items-start justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">{tipo.nome}</h3>
                    {tipo.descricao && (
                      <p className="text-sm text-slate-600 mt-1">{tipo.descricao}</p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(tipo)}
                      className="flex items-center gap-1"
                    >
                      <Edit2 className="h-3 w-3" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(tipo.id)}
                      className="flex items-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Deletar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="bg-orange-600 text-white p-4 rounded-t-lg">
              <h3 className="text-xl font-bold">
                {editingId ? 'Editar Tipo de Demissão' : 'Novo Tipo de Demissão'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome <span className="text-red-500">*</span></Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: Demissão Sem Justa Causa"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Descrição opcional do tipo de demissão"
                  rows={3}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1">
                  <Save className="h-4 w-4 mr-2" />
                  {editingId ? 'Atualizar' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiposDemissaoPage;
